import React from "react";

/**
 * App-wide error boundary. If ANY React subtree throws during render or in a
 * lifecycle hook, this catches it and renders a friendly fallback with a reload
 * button — so the user NEVER sees a blank white page.
 *
 * Triggered by uncaught errors in render(), constructor(), or lifecycle methods
 * of descendants. Does NOT catch errors in event handlers or async code — those
 * are swallowed by React anyway and won't blank the page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console so devtools and remote log capture see it
    console.error("[ErrorBoundary] React render crash:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHardReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = String(this.state.error?.message || this.state.error || "Unknown render error");

    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white border border-zinc-200 p-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 text-white bg-[#C8102E]">
            Something broke
          </div>
          <h1 className="font-display font-black text-2xl tracking-tight mt-4">
            We hit a snag rendering this page.
          </h1>
          <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
            Don&apos;t worry — your data is safe. This is usually a stale browser cache after a fresh deploy.
            Try reloading the page. If it keeps happening, log out and back in.
          </p>
          <div className="mt-4 p-3 bg-zinc-100 border border-zinc-200 font-mono text-[11px] text-zinc-700 break-words" data-testid="error-boundary-message">
            {msg}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={this.handleHardReload}
              data-testid="error-boundary-reload"
              className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold"
            >
              Reload page
            </button>
            <button
              onClick={this.handleReset}
              data-testid="error-boundary-dismiss"
              className="text-sm text-zinc-600 hover:text-zinc-900 font-semibold px-2"
            >
              Try again
            </button>
            <a href="/" className="text-sm text-zinc-500 hover:text-[#C8102E] ml-auto" data-testid="error-boundary-home">
              ← Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
